import { Rectangle } from "@/types";

export const getDataUrlFromFile = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const result = e.target?.result;

      if (typeof result === "string") {
        resolve(result);
      } else {
        reject(new Error("Failed to read file"));
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsDataURL(file);
  });
};

export const getPNGFromImgBlob = async (blob: Blob): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    if (!blob.type.includes("image")) {
      reject(new Error("Input blob not an image"));
      return;
    }

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      reject(new Error("Canvas context not available"));
      return;
    }

    const img = new Image();
    const blobUrl = URL.createObjectURL(blob);

    img.onload = () => {
      try {
        canvas.width = img.width;
        canvas.height = img.height;

        ctx.drawImage(img, 0, 0);

        canvas.toBlob(function (resultBlob) {
          if (resultBlob) {
            resolve(resultBlob);
          } else {
            reject(new Error("Failed to convert image to PNG"));
          }
        }, "image/png");
      } catch (error) {
        URL.revokeObjectURL(blobUrl);
        reject(error);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(blobUrl);
      reject(new Error("Image loading error"));
    };

    img.src = blobUrl;
  });
};

export const getImageObjectSafe = async (
  imageUrl: string,
): Promise<HTMLImageElement | null> => {
  try {
    return await getImageObject(imageUrl);
  } catch {
    return null;
  }
};

export const getImageObject = (imageUrl: string): Promise<HTMLImageElement> =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
    img.crossOrigin = "anonymous";
    img.src = imageUrl;
  });

// Creates a css clip path for a cropped image based on the image aspect ratio and the container aspect ratio
const calculateCropPadding = (
  image: HTMLImageElement,
  crop: Rectangle,
  containerAspectRatio: number,
) => {
  const imageAspectRatio = image.width / image.height;
  const cropXRatio = crop.x / image.width;
  const cropYRatio = crop.y / image.height;
  const cropWidthRatio = crop.width / image.width;
  const cropHeightRatio = crop.height / image.height;
  // get frame - the larger of crop box and image box
  // position image inside frame - with padding if needed
  // position crop box inside the frame - with clipPath
  let imageDisplayWidth: number,
    imageDisplayHeight: number,
    offsetX: number,
    offsetY: number;

  if (imageAspectRatio < containerAspectRatio) {
    // Portrait image: height fills container, width is smaller
    imageDisplayHeight = 100;
    imageDisplayWidth = (imageAspectRatio / containerAspectRatio) * 100;
    offsetX = (100 - imageDisplayWidth) / 2;
    offsetY = 0;
  } else {
    // Landscape image: width fills container, height is smaller
    imageDisplayWidth = 100;
    imageDisplayHeight = (containerAspectRatio / imageAspectRatio) * 100;
    offsetX = 0;
    offsetY = (100 - imageDisplayHeight) / 2;
  }

  // Convert crop percentages from image space to display space
  const displayX = offsetX + cropXRatio * imageDisplayWidth;
  const displayY = offsetY + cropYRatio * imageDisplayHeight;
  const displayWidth = cropWidthRatio * imageDisplayWidth;
  const displayHeight = cropHeightRatio * imageDisplayHeight;

  // Get the normalized frame that contains both image and crop box
  const imageFrame = {
    top: offsetY,
    right: 100 - (offsetX + imageDisplayWidth),
    bottom: 100 - (offsetY + imageDisplayHeight),
    left: offsetX,
  };
  const cropFrame = {
    top: displayY,
    right: 100 - (displayX + displayWidth),
    bottom: 100 - (displayY + displayHeight),
    left: displayX,
  };
  const normalizedFrame = {
    top: Math.min(0, imageFrame.top, cropFrame.top),
    right: Math.min(0, imageFrame.right, cropFrame.right),
    bottom: Math.min(0, imageFrame.bottom, cropFrame.bottom),
    left: Math.min(0, imageFrame.left, cropFrame.left),
  };
  let normalizedWidth = 100 - normalizedFrame.left - normalizedFrame.right;
  let normalizedHeight = 100 - normalizedFrame.top - normalizedFrame.bottom;

  // fix aspect ratio of normalized frame
  const normalizedAspectRatio = normalizedWidth / normalizedHeight;
  if (normalizedAspectRatio > containerAspectRatio) {
    // too wide, adjust height
    const desiredHeight = normalizedWidth / containerAspectRatio;
    const heightDiff = desiredHeight - normalizedHeight;
    normalizedFrame.top -= heightDiff / 2;
    normalizedFrame.bottom -= heightDiff / 2;
    normalizedHeight = desiredHeight;
  } else {
    // too tall, adjust width
    const desiredWidth = normalizedHeight * containerAspectRatio;
    const widthDiff = desiredWidth - normalizedWidth;
    normalizedFrame.left -= widthDiff / 2;
    normalizedFrame.right -= widthDiff / 2;
    normalizedWidth = desiredWidth;
  }

  // Adjust frames to be relative to normalized frame
  const adjustFrame = (frame: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  }) => ({
    top: ((frame.top - normalizedFrame.top) / normalizedHeight) * 100,
    right: ((frame.right - normalizedFrame.right) / normalizedWidth) * 100,
    bottom: ((frame.bottom - normalizedFrame.bottom) / normalizedHeight) * 100,
    left: ((frame.left - normalizedFrame.left) / normalizedWidth) * 100,
  });

  return {
    imageFrame: adjustFrame(imageFrame),
    cropFrame: adjustFrame(cropFrame),
  };
};

// Creates a css clip path for a cropped image based on the image aspect ratio and the container aspect ratio
export const calculateTransformCSS = (
  image: HTMLImageElement,
  crop: Rectangle,
  containerAspectRatio: number,
) => {
  const { imageFrame, cropFrame } = calculateCropPadding(
    image,
    crop,
    containerAspectRatio,
  );
  return {
    imageFrame: {
      top: `${imageFrame.top}%`,
      right: `${imageFrame.right}%`,
      bottom: `${imageFrame.bottom}%`,
      left: `${imageFrame.left}%`,
      width: `${100 - imageFrame.left - imageFrame.right}%`,
      height: `${100 - imageFrame.top - imageFrame.bottom}%`,
    },
    clipPath: `polygon(
      0% 0%,
      100% 0%,
      100% 100%,
      0% 100%,
      0% 0%,
      ${cropFrame.left}% ${cropFrame.top}%,
      ${cropFrame.left}% ${100 - cropFrame.bottom}%,
      ${100 - cropFrame.right}% ${100 - cropFrame.bottom}%,
      ${100 - cropFrame.right}% ${cropFrame.top}%,
      ${cropFrame.left}% ${cropFrame.top}%
    )`,
  };
};
